import type React from 'react';

import { colors } from '@packages/core/styles';
import { WorksheetAnswerStatus } from '@packages/models/api';
import { Icon, TextBadge, Typography } from '@packages/ui/shared';
import { getWorksheetBadgeVariant } from '@web/utils';
import { formatDate } from '@web/utils/date-format';
import { useCallback, useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { TouchableOpacity } from 'react-native';

import type { Worksheet } from './worksheet-group-accordion.types';

/** date-fns format used for the last-updated stamp shown next to multi-instance worksheet names. */
const MULTI_INSTANCE_DATE_FORMAT = "MMM d, yyyy '@' h:mm a";

/** Dynamic style props passed to useStyles for conditional border styling. */
type WorksheetItemCardStyleProps = {
    /** Whether the worksheet requires submission (e.g. Case is in Submitted status). */
    doesNeedSubmission: boolean;

    /** Whether this worksheet submission has missing required information. */
    hasMissingInfo: boolean;

    /** Whether this worksheet's checkbox is currently selected. */
    isSelected: boolean;

    /** Whether to apply selected-state border styling. */
    showSelectedStyling: boolean;
};

/** Inline style applied to submitted worksheet cards (reduced opacity, default cursor). */
const submittedCardStyle = { cursor: 'default', opacity: 0.7 } as const;

/** Empty style object for non-submitted worksheet cards. */
const defaultCardStyle = {} as const;

/** Inline style for the required field asterisk indicator. */
const requiredAsteriskStyle = { color: '#d32f2f', marginLeft: 4 } as const;

/** Inline style for disabled action icons (edit/delete on submitted worksheets). */
const disabledIconStyle = { cursor: 'not-allowed', opacity: 0.4 } as const;

/** Props for the WorksheetItemCard component. */
export type WorksheetItemCardProps = {
    /** Whether the worksheet requires submission (e.g. Case is in Submitted status). */
    doesNeedSubmission?: boolean;

    /** Whether this worksheet submission has missing required information. */
    hasMissingInfo?: boolean;

    /** Whether the parent case is in a closed status (CLOSED, CLOSED-RESOLVED, etc.). */
    isCaseClosed?: boolean;

    /** Whether the parent case is in DRAFT status. */
    isDraftCase?: boolean;

    /** Whether this worksheet is marked as required (shown with asterisk). */
    isRequired?: boolean;

    /** Whether this worksheet's checkbox is currently selected. */
    isSelected: boolean;

    /** Callback to navigate to the worksheet form for completion. */
    onCompleteWorksheet: (worksheetId: string) => Promise<void>;

    /** Callback to delete a worksheet submission. */
    onDeleteWorksheet?: (worksheetId: string, submissionId: string) => void;

    /** Callback to download a worksheet submission as a file. */
    onDownloadWorksheet?: (worksheetId: string, submissionId: string) => Promise<void>;

    /** Callback to open a worksheet submission for editing. */
    onEditWorksheet?: (worksheetId: string, submissionId: string) => Promise<void>;

    /** Callback to toggle this worksheet's selected state. */
    onSelect: (worksheetId: string, isSelected: boolean) => void;

    /** Callback to view a submitted worksheet in read-only mode. */
    onViewWorksheet?: (worksheetId: string, submissionId: string) => Promise<void>;

    /** Whether to apply selected-state border styling when the worksheet is selected. */
    showSelectedStyling?: boolean;

    /** The worksheet data to display. */
    worksheet: Worksheet;
};

/**
 * Card component for a single worksheet within a group accordion. Renders a checkbox,
 * the worksheet name and description, and contextual action buttons (complete, edit,
 * delete, view) based on the worksheet's submission status.
 *
 * @param props - component props
 * @returns the worksheet item card
 */
export function WorksheetItemCard({
    doesNeedSubmission = false,
    hasMissingInfo = false,
    isCaseClosed = false,
    isDraftCase = false,
    isRequired = false,
    isSelected,
    onCompleteWorksheet,
    onDeleteWorksheet,
    onEditWorksheet,
    onSelect,
    onViewWorksheet,
    showSelectedStyling = true,
    worksheet,
}: WorksheetItemCardProps) {
    const classes = useStyles({ doesNeedSubmission, hasMissingInfo, isSelected, showSelectedStyling });
    const isSubmitted = Boolean(worksheet.submissionId) && Boolean(worksheet.status);

    // Derived state

    /**
     * Display name with the last-updated date appended for worksheets that have multiple
     * submissions in their group, so users can distinguish otherwise identical rows
     * (TECHSHARE-6411). Falls back to the bare display name when the worksheet is the only
     * instance or has no timestamp.
     */
    const displayNameWithDate = useMemo(() => {
        if (!worksheet.hasMultipleSubmissions || !worksheet.lastUpdatedTimestamp) {
            return worksheet.displayName;
        }
        return `${worksheet.displayName} (${formatDate(worksheet.lastUpdatedTimestamp, MULTI_INSTANCE_DATE_FORMAT)})`;
    }, [worksheet.displayName, worksheet.hasMultipleSubmissions, worksheet.lastUpdatedTimestamp]);

    // Handlers

    /**
     * Handle the worksheet checkbox change, toggling selection for non-submitted worksheets.
     *
     * @param e - the checkbox change event
     */
    const handleCheckboxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (!isSubmitted) {
                onSelect(worksheet.worksheetId, e.target.checked);
            }
        },
        [isSubmitted, onSelect, worksheet.worksheetId]
    );

    /** Toggle the worksheet selection when the card body is clicked. */
    const handleCardClick = useCallback(() => {
        if (!isSubmitted) {
            onSelect(worksheet.worksheetId, !isSelected);
        }
    }, [isSelected, isSubmitted, onSelect, worksheet.worksheetId]);

    /**
     * Handle the "Complete Worksheet" button click, stopping propagation
     * to prevent the card click from toggling selection.
     *
     * @param e - the click event
     */
    const handleCompleteWorksheet = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            await onCompleteWorksheet(worksheet.worksheetId);
        },
        [onCompleteWorksheet, worksheet.worksheetId]
    );

    /** Open the worksheet for editing if it has a submission. */
    const handleEditWorksheet = useCallback(async () => {
        if (onEditWorksheet && worksheet.submissionId) {
            await onEditWorksheet(worksheet.worksheetId, worksheet.submissionId);
        }
    }, [onEditWorksheet, worksheet.submissionId, worksheet.worksheetId]);

    /** Delete the worksheet submission if it exists. */
    const handleDeleteWorksheet = useCallback(() => {
        if (onDeleteWorksheet && worksheet.submissionId) {
            onDeleteWorksheet(worksheet.worksheetId, worksheet.submissionId);
        }
    }, [onDeleteWorksheet, worksheet.submissionId, worksheet.worksheetId]);

    /** View the submitted worksheet in read-only mode. */
    const handleViewWorksheet = useCallback(async () => {
        if (onViewWorksheet && worksheet.submissionId) {
            await onViewWorksheet(worksheet.worksheetId, worksheet.submissionId);
        }
    }, [onViewWorksheet, worksheet.submissionId, worksheet.worksheetId]);

    /**
     * Handle keyboard navigation on the card. Space or Enter toggles selection.
     *
     * @param e - the keyboard event
     */
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                handleCardClick();
            }
        },
        [handleCardClick]
    );

    return (
        <>
            <div
                aria-pressed={isSelected}
                className={classes.worksheetCard}
                data-worksheet-id={worksheet.worksheetId}
                onClick={handleCardClick}
                onKeyDown={handleKeyDown}
                role="button"
                style={
                    isSubmitted && worksheet.status === WorksheetAnswerStatus.Submitted
                        ? submittedCardStyle
                        : defaultCardStyle
                }
                tabIndex={isSubmitted ? -1 : 0}
            >
                <div className={classes.checkboxContainer} onClick={stopPropagation}>
                    <Form.Check
                        checked={isSelected}
                        disabled={isSubmitted}
                        id={`worksheet-${worksheet.worksheetId}`}
                        label=""
                        onChange={handleCheckboxChange}
                        tabIndex={-1}
                        type="checkbox"
                    />
                </div>
                <div className={classes.worksheetContent}>
                    <div className={classes.worksheetName}>
                        <Typography color="textDarkPrimary" variant="h6">
                            {displayNameWithDate}
                        </Typography>
                        {isRequired && !isSubmitted && <span style={requiredAsteriskStyle}>*</span>}
                    </div>
                    <div className={classes.worksheetDescription}>
                        <Typography color="textDarkSecondary">{worksheet.worksheetDescription}</Typography>
                    </div>
                    {worksheet.worksheetError && <div className={classes.errorMessage}>{worksheet.worksheetError}</div>}
                </div>
                {isCaseClosed && isSubmitted ? (
                    // Closed cases: view-only for all worksheets regardless of submission status
                    <div className={classes.submittedActionsContainer}>
                        <TextBadge variant={getWorksheetBadgeVariant(worksheet.status || '')}>
                            {worksheet.status}
                        </TextBadge>
                        <div className={classes.actionButtons}>
                            <TouchableOpacity onPress={handleViewWorksheet}>
                                <Icon color="blueOne" name="eye" size={20} />
                            </TouchableOpacity>
                        </div>
                    </div>
                ) : isSubmitted ? (
                    <div className={classes.submittedActionsContainer}>
                        <TextBadge variant={getWorksheetBadgeVariant(worksheet.status || '')}>
                            {worksheet.status}
                        </TextBadge>
                        {worksheet.status === WorksheetAnswerStatus.Draft ||
                        worksheet.status === WorksheetAnswerStatus.Uploaded ||
                        (worksheet.status === WorksheetAnswerStatus.Submitted && isDraftCase) ? (
                            <div className={classes.actionButtons}>
                                <TouchableOpacity onPress={handleEditWorksheet}>
                                    <Icon color="blueOne" name="pencil" size={20} />
                                </TouchableOpacity>
                                <TouchableOpacity onPress={handleDeleteWorksheet}>
                                    <Icon color="blueOne" name="trash-can" size={20} />
                                </TouchableOpacity>
                            </div>
                        ) : worksheet.status === WorksheetAnswerStatus.Submitted ? (
                            <div className={classes.actionButtons}>
                                <TouchableOpacity onPress={handleViewWorksheet}>
                                    <Icon color="blueOne" name="eye" size={20} />
                                </TouchableOpacity>
                                <div style={disabledIconStyle}>
                                    <Icon color="grayFive" name="pencil" size={20} />
                                </div>
                                <div style={disabledIconStyle}>
                                    <Icon color="grayFive" name="trash-can" size={20} />
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    isSelected && (
                        <Button
                            className={classes.completeButton}
                            disabled={worksheet.isLoadingWorksheet}
                            onClick={handleCompleteWorksheet}
                            variant="primary"
                        >
                            {worksheet.isLoadingWorksheet && <span className={classes.loadingSpinner} />}
                            {worksheet.isLoadingWorksheet ? 'Loading...' : 'Complete Worksheet'}
                        </Button>
                    )
                )}
            </div>
            {hasMissingInfo && <div className={classes.missingInfoMessage}>Required information is missing</div>}
            {doesNeedSubmission && (
                <div className={classes.missingInfoMessage}>
                    This worksheet has not been submitted. Please submit it before closing the case.
                </div>
            )}
        </>
    );
}

/**
 * Stop a click event from propagating to parent handlers. Used on the checkbox
 * container to prevent the card click handler from toggling selection.
 *
 * @param e - the click event
 */
function stopPropagation(e: React.MouseEvent) {
    e.stopPropagation();
}

const useStyles = createUseStyles({
    worksheetCard: {
        backgroundColor: colors.white,
        borderRadius: 8,
        padding: '20px 24px',
        minHeight: 82,
        marginBottom: 12,
        display: 'grid',
        gridTemplateColumns: '24px 1fr auto',
        gap: 16,
        alignItems: 'start',
        border: (props: WorksheetItemCardStyleProps) =>
            props.hasMissingInfo || props.doesNeedSubmission
                ? `1px solid ${colors.redOne}`
                : props.isSelected && props.showSelectedStyling
                  ? `1px solid ${colors.blueOne}`
                  : `1px solid ${colors.grayThree}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: (props: WorksheetItemCardStyleProps) =>
                props.hasMissingInfo || props.doesNeedSubmission ? colors.redOne : colors.grayFour,
        },
        '&:last-child': {
            marginBottom: 0,
        },
    },
    checkboxContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        height: 24,
        '& .form-check': {
            marginTop: 0,
            paddingTop: 0,
            height: 24,
            display: 'flex',
            alignItems: 'center',
        },
        '& .form-check-label': {
            display: 'inline',
            padding: '3px 0 !important',
        },
        '& .form-check-input': {
            marginTop: 0,
        },
        '& .form-check-input:disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
            backgroundColor: colors.grayTwo,
            borderColor: colors.grayFour,
        },
    },
    worksheetContent: {
        display: 'flex',
        flexDirection: 'column',
    },
    worksheetName: {
        marginBottom: 2,
    },
    worksheetDescription: {
        color: colors.grayFive,
    },
    completeButton: {
        padding: '8px 16px',
        fontSize: 14,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        alignSelf: 'start',
    },
    errorMessage: {
        marginTop: 8,
        padding: 12,
        backgroundColor: colors.redOne,
        color: colors.white,
        borderRadius: 4,
        fontSize: 14,
        lineHeight: '18px',
    },
    loadingSpinner: {
        display: 'inline-block',
        width: 16,
        height: 16,
        border: `2px solid ${colors.white}`,
        borderTop: `2px solid transparent`,
        borderRadius: '50%',
        animation: '$spin 0.8s linear infinite',
        marginRight: 8,
    },
    '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' },
    },
    submittedActionsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        alignSelf: 'start',
    },
    actionButtons: {
        display: 'flex',
        gap: 12,
        alignItems: 'center',
    },
    iconButton: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
            opacity: 0.7,
        },
    },
    missingInfoMessage: {
        marginTop: -8,
        marginBottom: 12,
        paddingLeft: 24,
        fontSize: 13,
        fontWeight: 400,
        color: colors.redOne,
        lineHeight: '18px',
    },
});
